import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, CheckCircle, ArrowRight } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from 'sonner';
import { TIPOS, PRIORIDADES, AREAS_QTRO } from './constants';
import { TicketList } from './TicketList';
import './NewsTicket.css';

interface Question {
  id: string;
  question: string;
  type: 'select' | 'text' | 'textarea' | 'email' | 'file';
  options?: string[];
  placeholder?: string;
  validation?: RegExp;
  required?: boolean;
}

const questions: Question[] = [
  {
    id: 'tipo',
    question: 'O que você precisa?',
    type: 'select',
    options: TIPOS as unknown as string[],
    required: true
  },
  {
    id: 'contexto',
    question: 'Como está no momento e o que está acontecendo?',
    type: 'textarea',
    placeholder: 'Descreva o contexto atual...',
    required: true
  },
  {
    id: 'desafio',
    question: 'Qual desafio você está enfrentando?',
    type: 'textarea',
    placeholder: 'Conte-nos sobre seu desafio...',
    required: true
  },
  {
    id: 'solicitacao',
    question: 'O que você precisa que seja feito?',
    type: 'textarea',
    placeholder: 'Descreva sua solicitação...',
    required: true
  },
  {
    id: 'prioridade',
    question: 'Qual a prioridade do seu pedido?',
    type: 'select',
    options: PRIORIDADES.map(p => `${p.valor} - ${p.descricao}`),
    required: true
  },
  {
    id: 'area_qtro',
    question: 'Qual sua área QTRO?',
    type: 'select',
    options: AREAS_QTRO as unknown as string[],
    required: true
  },
  {
    id: 'email',
    question: 'Qual seu e-mail corporativo?',
    type: 'email',
    placeholder: '@waffle.com.br ou @thenewscc.com.br',
    validation: /@(waffle\.com\.br|thenewscc\.com\.br)$/,
    required: true
  },
  {
    id: 'arquivos',
    question: 'Gostaria de anexar algum arquivo importante?',
    type: 'file',
    required: false
  }
];

export default function NewsTicket() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mostrarConsulta, setMostrarConsulta] = useState(false);
  const [emailConsulta, setEmailConsulta] = useState('');

  const currentQuestion = questions[currentQuestionIndex];

  const validateAnswer = (questionId: string, answer: any): boolean => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return false;
    
    if (question.required && (!answer || (typeof answer === 'string' && answer.trim() === ''))) {
      setError('Este campo é obrigatório');
      return false;
    }

    if (question.validation && typeof answer === 'string' && !question.validation.test(answer)) {
      setError('Formato inválido');
      return false;
    }

    setError(null);
    return true;
  };

  const handleNextQuestion = () => {
    const currentAnswer = answers[currentQuestion.id];
    if (!validateAnswer(currentQuestion.id, currentAnswer)) {
      return;
    }

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setError(null);
    }
  };

  const handleAnswer = (questionId: string, answer: any) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    setError(null);
    
    // Para select, valida e avança automaticamente
    if (currentQuestion.type === 'select') {
      // Validação acontece após salvar a resposta
      if (validateAnswer(questionId, answer)) {
        if (currentQuestionIndex < questions.length - 1) {
          setCurrentQuestionIndex(prev => prev + 1);
        }
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setFiles(prev => [...prev, ...filesArray]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('https://backend.testeswaffle.org/tickets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: answers.tipo,
          contexto: answers.contexto,
          desafio: answers.desafio,
          solicitacao: answers.solicitacao,
          prioridade: answers.prioridade?.split(' - ')[0],
          area_qtro: answers.area_qtro,
          email: answers.email
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao enviar o ticket');
      }

      const result = await response.json();

      if (files.length > 0) {
        const formData = new FormData();
        files.forEach(file => {
          formData.append('arquivos', file);
        });

        await fetch(`https://backend.testeswaffle.org/tickets/${result.data.id}/arquivos`, {
          method: 'POST',
          body: formData
        });
      }

      toast.success('Ticket criado com sucesso! Em breve entraremos em contato.');
      setAnswers({});
      setFiles([]);
      setCurrentQuestionIndex(0);
    } catch (error) {
      toast.error('Erro ao criar o ticket. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white flex items-center justify-center pt-8">
      <div className="w-full max-w-3xl p-6">
        {!mostrarConsulta ? (
          <>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12 text-center"
            >
              <img 
                src="/logo.png" 
                alt="the news"
                className="w-48 mx-auto"
              />
            </motion.div>

            {/* <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-8 text-center"
            >
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 1 }}
                className="text-gray-600 leading-relaxed"
              >
                {[
                  "Um ticket tech é uma ferramenta essencial para reportar problemas, solicitar melhorias ou criar novas funcionalidades. Aqui você pode detalhar suas necessidades técnicas, anexar evidências e acompanhar todo o processo de resolução. Nosso time está pronto para te ajudar! 🚀"
                ].map((text, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.8, duration: 0.5 }}
                    className="block mb-2"
                  >
                    {text}
                  </motion.span>
                ))}
              </motion.p>
            </motion.div> */}

            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestionIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-8">
                  {currentQuestion?.question}
                </h2>

                <div className="space-y-4">
                  {currentQuestion?.type === 'select' && (
                    <div className="grid gap-4">
                      {currentQuestion.options?.map((option) => (
                        <button
                          key={option}
                          onClick={() => handleAnswer(currentQuestion.id, option)}
                          className="text-left p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all text-gray-900 border border-gray-200"
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}

                  {currentQuestion?.type === 'textarea' && (
                    <div className="space-y-4">
                      <textarea
                        placeholder={currentQuestion.placeholder}
                        onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                        value={answers[currentQuestion.id] || ''}
                        className="w-full h-40 bg-gray-50 border border-gray-200 rounded-xl p-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <Button
                        onClick={handleNextQuestion}
                        className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                      >
                        Continuar
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  )}

                  {currentQuestion?.type === 'email' && (
                    <div className="space-y-4">
                      <Input
                        type="email"
                        placeholder={currentQuestion.placeholder}
                        onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                        value={answers[currentQuestion.id] || ''}
                        className="bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500"
                      />
                      <Button
                        onClick={handleNextQuestion}
                        className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                      >
                        Continuar
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  )}

                  {currentQuestion?.type === 'file' && (
                    <div className="space-y-4">
                      <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer hover:bg-gray-50 transition-all">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Upload className="w-10 h-10 mb-4 text-gray-500" />
                          <p className="mb-2 text-sm text-gray-500">
                            Arraste arquivos ou clique para fazer upload
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          multiple
                          onChange={handleFileChange}
                          accept="image/*,.pdf,.doc,.docx"
                        />
                      </label>

                      {files.length > 0 && (
                        <div className="space-y-2">
                          {files.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl">
                              <span className="text-gray-900 truncate">{file.name}</span>
                              <button
                                onClick={() => setFiles(files.filter((_, i) => i !== index))}
                                className="text-red-500 hover:text-red-600"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {currentQuestionIndex === questions.length - 1 ? (
                        <Button
                          onClick={handleSubmit}
                          disabled={loading}
                          className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                        >
                          {loading ? (
                            <span className="flex items-center justify-center gap-2">
                              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Enviando...
                            </span>
                          ) : (
                            <span className="flex items-center justify-center gap-2">
                              <CheckCircle size={20} />
                              Enviar Ticket
                            </span>
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={handleNextQuestion}
                          className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                        >
                          Continuar
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {error && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-2 text-sm text-red-500"
                  >
                    {error}
                  </motion.p>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-8 flex justify-between items-center text-gray-500">
              <button
                onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                disabled={currentQuestionIndex === 0}
                className="text-sm hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Anterior
              </button>
              <div className="flex items-center space-x-2">
                <div className="h-1 w-32 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gray-900 transition-all duration-300"
                    style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                  />
                </div>
                <span className="text-sm">
                  {currentQuestionIndex + 1} de {questions.length}
                </span>
              </div>
              <div className="w-16" />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-8 text-center"
            >
              <button
                onClick={() => setMostrarConsulta(true)}
                className="text-sm text-pink-500 hover:text-pink-600 transition-colors duration-200 underline"
              >
                Já tem um ticket? Clique aqui para consultar →
              </button>
            </motion.div>
          </>
        ) : (
          <>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12 text-center"
            >
              <img 
                src="/logo.png" 
                alt="the news"
                className="w-48 mx-auto mb-8"
              />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Consulta de Tickets
              </h2>
            </motion.div>

            <div className="mb-8">
              <Input
                type="email"
                placeholder="Digite seu e-mail corporativo"
                value={emailConsulta}
                onChange={(e) => setEmailConsulta(e.target.value)}
                pattern=".*@(waffle\.com\.br|thenewscc\.com\.br)$"
                className="mb-4"
              />
              <Button
                onClick={() => setEmailConsulta(emailConsulta)}
                disabled={!emailConsulta.match(/@(waffle\.com\.br|thenewscc\.com\.br)$/)}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white"
              >
                Consultar Tickets
              </Button>
            </div>

            {emailConsulta && <TicketList email={emailConsulta} showProtocol={true} />}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-8 text-center"
            >
              <button
                onClick={() => {
                  setMostrarConsulta(false);
                  setEmailConsulta('');
                }}
                className="text-sm text-pink-500 hover:text-pink-600 transition-colors duration-200 underline"
              >
                ← Voltar para criação de ticket
              </button>
            </motion.div>
          </>
        )}

        <motion.div 
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <p className="text-[#4D4D4D] text-sm font-['Shrikhand']">
            feito com 🧇 pelo <span className="text-pink-500">geraldão</span> • 2025
          </p>
        </motion.div>
      </div>
    </div>
  );
} 